import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { setOptions, chartConfig } from '../utils/chartConfigs';
import { colors } from '../utils/colors';
import {
  getDataSet, getSumDataSet, getFilteredData, getFilteredLabels,
  setDatesAsWeeks, setDatesAsMonths, getDataSetGrouped, groupBy, getDataSetGroupedAvg,
} from '../utils/dataManager';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const STACKED = true;
const FILL = true;
const CUSTOMHEIGHT = true;

const DashboardFilteredAndGrouped = ({ filter, group }) => {
  const { analyticsData, errors } = useDashboardData();
  const { flagsPerDay, songsPerDay, analyticsEventsPerDay, usersPerDay, subscribersPerDay } = analyticsData;
  const location = useLocation();

  const flagsFiltered = getFilteredData(flagsPerDay, filter);
  const songsFiltered = getFilteredData(songsPerDay, filter);
  const analyticsEventsFiltered = getFilteredData(analyticsEventsPerDay, filter);
  const usersFiltered = getFilteredData(usersPerDay, filter);
  const subscribersFiltered = getFilteredData(subscribersPerDay, filter);

  let flagsGrouped = [], songsGrouped = [], analyticsEventsGrouped = [], usersGrouped = [], subscribersGrouped = [];

  switch (group) {
    case 'week':
      flagsGrouped = groupBy(setDatesAsWeeks(flagsFiltered, filter), 'creation_date');
      songsGrouped = groupBy(setDatesAsWeeks(songsFiltered, filter), 'creation_date');
      analyticsEventsGrouped = groupBy(setDatesAsWeeks(analyticsEventsFiltered, filter), 'creation_date');
      usersGrouped = groupBy(setDatesAsWeeks(usersFiltered, filter), 'creation_date');
      subscribersGrouped = groupBy(setDatesAsWeeks(subscribersFiltered, filter), 'creation_date');
      break;
    case 'month':
      flagsGrouped = groupBy(setDatesAsMonths(flagsFiltered), 'creation_date');
      songsGrouped = groupBy(setDatesAsMonths(songsFiltered), 'creation_date');
      analyticsEventsGrouped = groupBy(setDatesAsMonths(analyticsEventsFiltered), 'creation_date');
      usersGrouped = groupBy(setDatesAsMonths(usersFiltered), 'creation_date');
      subscribersGrouped = groupBy(setDatesAsMonths(subscribersFiltered), 'creation_date');
      break;
    default:
      break;
  }

  const getDataSetFromFiltered = () => ({
    flagsLabels: getFilteredLabels(flagsFiltered),
    songsLabels: getFilteredLabels(songsFiltered),
    analyticsEventsLabels: getFilteredLabels(analyticsEventsFiltered),
    usersLabels: getFilteredLabels(usersFiltered),
    subscribersLabels: getFilteredLabels(subscribersFiltered),
    newSongwriterSongs: getDataSet(songsFiltered, 'appname_songwriter'),
    newStudioSongs: getDataSet(songsFiltered, 'appname_studio'),
    newExpressSongs: getDataSet(songsFiltered, 'source_type_recording'),
    newStudioAndSongwriterSongs: getDataSet(songsFiltered, 'source_type_manual'),
    paidSubscriptionSongwriter: getDataSet(flagsFiltered, 'subscription_paid_songwriter'),
    trialSubscriptionSongwriter: getDataSet(flagsFiltered, 'subscription_trial_songwriter'),
    paidSubscriptionPlus: getDataSet(flagsFiltered, 'subscription_paid_plus'),
    trialSubscriptionPlus: getDataSet(flagsFiltered, 'subscription_trial_plus'),
    paidSubscriptionPro: getDataSet(flagsFiltered, 'subscription_paid_pro'),
    trialSubscriptionPro: getDataSet(flagsFiltered, 'subscription_trial_pro'),
    loginSongwriter: getDataSet(flagsFiltered, 'login_songwriter'),
    loginSongwriterWin: getDataSet(flagsFiltered, 'login_songwriter_win'),
    loginSongwriterOsx: getDataSet(flagsFiltered, 'login_songwriter_osx'),
    loginSongwriterOsxAppstore: getDataSet(flagsFiltered, 'login_songwriter_osx_appstore'),
    loginStudio: getDataSet(flagsFiltered, 'login_studio'),
    loginStudioWin: getDataSet(flagsFiltered, 'login_studio_win'),
    loginStudioOsx: getDataSet(flagsFiltered, 'login_studio_osx'),
    loginStudioOsxAppstore: getDataSet(flagsFiltered, 'login_studio_osx_appstore'),
    loginExpress: getDataSet(flagsFiltered, 'login_express'),
    loginExpressIpad: getDataSet(flagsFiltered, 'login_express_ipad'),
    loginExpressIphone: getDataSet(flagsFiltered, 'login_express_iphone'),
    loginMyScorecloud: getDataSet(flagsFiltered, 'login_my_scorecloud'),
    recordingStart: getDataSet(analyticsEventsFiltered, 'recording_start'),
    audioImport: getDataSet(analyticsEventsFiltered, 'audio_import'),
    videoImport: getDataSet(analyticsEventsFiltered, 'video_import'),
    songDisplay: getDataSet(analyticsEventsFiltered, 'song_display'),
    songDisplayYouTube: getDataSet(analyticsEventsFiltered, 'youtube_song_display'),
    recordingPlusImport: getSumDataSet([getDataSet(analyticsEventsFiltered, 'recording_start'), getDataSet(analyticsEventsFiltered, 'audio_import'), getDataSet(analyticsEventsFiltered, 'video_import')]),
    programStartSongwriter: getDataSet(analyticsEventsFiltered, 'program_start'),
    totalUsers: getDataSet(usersFiltered, 'total_users'),
    usersMyScorecloud: getDataSet(usersFiltered, 'registration_app_myscorecloud'),
    usersStudio: getDataSet(usersFiltered, 'registration_app_studio'),
    usersExpress: getDataSet(usersFiltered, 'registration_app_express'),
    usersUnknown: getDataSet(usersFiltered, 'registration_app_unknown'),
    usersSongwriter: getDataSet(usersFiltered, 'registration_app_songwriter'),
    subscriptionStart: getDataSet(analyticsEventsFiltered, 'subscription_start'),
    subscriptionUpgrade: getDataSet(analyticsEventsFiltered, 'subscription_upgrade'),
    subscriptionDowngrade: getDataSet(analyticsEventsFiltered, 'subscription_downgrade'),
    subscriptionCancelled: getDataSet(analyticsEventsFiltered, 'subscription_cancelled'),
    subscriptionChargedSuccessfully: getDataSet(analyticsEventsFiltered, 'subscription_charged_successfully'),
    webDownloadSongwriterMac: getDataSet(analyticsEventsFiltered, 'web_download_songwriter_mac'),
    webDownloadSongwriterWin: getDataSet(analyticsEventsFiltered, 'web_download_songwriter_windows'),
    webDownloadStudioMac: getDataSet(analyticsEventsFiltered, 'web_download_studio_mac'),
    webDownloadStudioWin: getDataSet(analyticsEventsFiltered, 'web_download_studio_windows'),
    subscriptionChargedSuccessfullySum: getDataSet(analyticsEventsFiltered, 'subscription_charged_successfully_sum'),
    subscriptionChargedSuccessfullyPlusSum: getDataSet(analyticsEventsFiltered, 'subscription_charged_successfully_plus_sum'),
    subscriptionChargedSuccessfullyProSum: getDataSet(analyticsEventsFiltered, 'subscription_charged_successfully_pro_sum'),
    subscriptionChargedSuccessfullySongwriterSum: getDataSet(analyticsEventsFiltered, 'subscription_charged_successfully_songwriter_sum'),
    activeUsersStudio: getDataSet(subscribersFiltered, 'active_users_studio'),
    activeUsersSongwriter: getDataSet(subscribersFiltered, 'active_users_songwriter'),
    activeUsersExpress: getDataSet(subscribersFiltered, 'active_users_express'),
    subscribersPaidPlus: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_paid_plus_monthly'), getDataSet(subscribersFiltered, 'subscribers_paid_plus_yearly')]),
    subscribersPaidSongwriter: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_paid_songwriter_monthly'), getDataSet(subscribersFiltered, 'subscribers_paid_songwriter_yearly')]),
    subscribersPaidPro: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_paid_pro_monthly'), getDataSet(subscribersFiltered, 'subscribers_paid_pro_yearly')]),
    subscribersTrialPlus: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_trial_plus_monthly'), getDataSet(subscribersFiltered, 'subscribers_trial_plus_yearly')]),
    subscribersTrialSongwriter: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_trial_songwriter_monthly'), getDataSet(subscribersFiltered, 'subscribers_trial_songwriter_yearly')]),
    subscribersTrialPro: getSumDataSet([getDataSet(subscribersFiltered, 'subscribers_trial_pro_monthly'), getDataSet(subscribersFiltered, 'subscribers_trial_pro_yearly')]),
  });

  const getDataSetFromFilteredAndGrouped = () => ({
    flagsLabels: Object.keys(flagsGrouped),
    songsLabels: Object.keys(songsGrouped),
    analyticsEventsLabels: Object.keys(analyticsEventsGrouped),
    usersLabels: Object.keys(usersGrouped),
    subscribersLabels: Object.keys(subscribersGrouped),
    newSongwriterSongs: getDataSetGrouped(songsGrouped, 'appname_songwriter'),
    newStudioSongs: getDataSetGrouped(songsGrouped, 'appname_studio'),
    newExpressSongs: getDataSetGrouped(songsGrouped, 'source_type_recording'),
    newStudioAndSongwriterSongs: getDataSetGrouped(songsGrouped, 'source_type_manual'),
    paidSubscriptionSongwriter: getDataSetGrouped(flagsGrouped, 'subscription_paid_songwriter'),
    trialSubscriptionSongwriter: getDataSetGrouped(flagsGrouped, 'subscription_trial_songwriter'),
    paidSubscriptionPlus: getDataSetGrouped(flagsGrouped, 'subscription_paid_plus'),
    trialSubscriptionPlus: getDataSetGrouped(flagsGrouped, 'subscription_trial_plus'),
    paidSubscriptionPro: getDataSetGrouped(flagsGrouped, 'subscription_paid_pro'),
    trialSubscriptionPro: getDataSetGrouped(flagsGrouped, 'subscription_trial_pro'),
    loginSongwriter: getDataSetGrouped(flagsGrouped, 'login_songwriter'),
    loginSongwriterWin: getDataSetGrouped(flagsGrouped, 'login_songwriter_win'),
    loginSongwriterOsx: getDataSetGrouped(flagsGrouped, 'login_songwriter_osx'),
    loginSongwriterOsxAppstore: getDataSetGrouped(flagsGrouped, 'login_songwriter_osx_appstore'),
    loginStudio: getDataSetGrouped(flagsGrouped, 'login_studio'),
    loginStudioWin: getDataSetGrouped(flagsGrouped, 'login_studio_win'),
    loginStudioOsx: getDataSetGrouped(flagsGrouped, 'login_studio_osx'),
    loginStudioOsxAppstore: getDataSetGrouped(flagsGrouped, 'login_studio_osx_appstore'),
    loginExpress: getDataSetGrouped(flagsGrouped, 'login_express'),
    loginExpressIpad: getDataSetGrouped(flagsGrouped, 'login_express_ipad'),
    loginExpressIphone: getDataSetGrouped(flagsGrouped, 'login_express_iphone'),
    loginMyScorecloud: getDataSetGrouped(flagsGrouped, 'login_my_scorecloud'),
    recordingStart: getDataSetGrouped(analyticsEventsGrouped, 'recording_start'),
    audioImport: getDataSetGrouped(analyticsEventsGrouped, 'audio_import'),
    videoImport: getDataSetGrouped(analyticsEventsGrouped, 'video_import'),
    songDisplay: getDataSetGrouped(analyticsEventsGrouped, 'song_display'),
    songDisplayYouTube: getDataSetGrouped(analyticsEventsGrouped, 'youtube_song_display'),
    recordingPlusImport: getSumDataSet([getDataSetGrouped(analyticsEventsGrouped, 'recording_start'), getDataSetGrouped(analyticsEventsGrouped, 'audio_import'), getDataSetGrouped(analyticsEventsGrouped, 'video_import')]),
    programStartSongwriter: getDataSetGrouped(analyticsEventsGrouped, 'program_start'),
    totalUsers: getDataSetGrouped(usersGrouped, 'total_users'),
    usersMyScorecloud: getDataSetGrouped(usersGrouped, 'registration_app_myscorecloud'),
    usersStudio: getDataSetGrouped(usersGrouped, 'registration_app_studio'),
    usersExpress: getDataSetGrouped(usersGrouped, 'registration_app_express'),
    usersUnknown: getDataSetGrouped(usersGrouped, 'registration_app_unknown'),
    usersSongwriter: getDataSetGrouped(usersGrouped, 'registration_app_songwriter'),
    subscriptionStart: getDataSetGrouped(analyticsEventsGrouped, 'subscription_start'),
    subscriptionUpgrade: getDataSetGrouped(analyticsEventsGrouped, 'subscription_upgrade'),
    subscriptionDowngrade: getDataSetGrouped(analyticsEventsGrouped, 'subscription_downgrade'),
    subscriptionCancelled: getDataSetGrouped(analyticsEventsGrouped, 'subscription_cancelled'),
    subscriptionChargedSuccessfully: getDataSetGrouped(analyticsEventsGrouped, 'subscription_charged_successfully'),
    webDownloadSongwriterMac: getDataSetGrouped(analyticsEventsGrouped, 'web_download_songwriter_mac'),
    webDownloadSongwriterWin: getDataSetGrouped(analyticsEventsGrouped, 'web_download_songwriter_windows'),
    webDownloadStudioMac: getDataSetGrouped(analyticsEventsGrouped, 'web_download_studio_mac'),
    webDownloadStudioWin: getDataSetGrouped(analyticsEventsGrouped, 'web_download_studio_windows'),
    subscriptionChargedSuccessfullySum: getDataSetGrouped(analyticsEventsGrouped, 'subscription_charged_successfully_sum'),
    subscriptionChargedSuccessfullyPlusSum: getDataSetGrouped(analyticsEventsGrouped, 'subscription_charged_successfully_plus_sum'),
    subscriptionChargedSuccessfullyProSum: getDataSetGrouped(analyticsEventsGrouped, 'subscription_charged_successfully_pro_sum'),
    subscriptionChargedSuccessfullySongwriterSum: getDataSetGrouped(analyticsEventsGrouped, 'subscription_charged_successfully_songwriter_sum'),
    activeUsersStudio: getDataSetGroupedAvg(subscribersGrouped, 'active_users_studio'),
    activeUsersSongwriter: getDataSetGroupedAvg(subscribersGrouped, 'active_users_songwriter'),
    activeUsersExpress: getDataSetGroupedAvg(subscribersGrouped, 'active_users_express'),
    subscribersPaidPlus: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_plus_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_plus_yearly')]),
    subscribersPaidSongwriter: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_songwriter_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_songwriter_yearly')]),
    subscribersPaidPro: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_pro_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_paid_pro_yearly')]),
    subscribersTrialPlus: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_plus_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_plus_yearly')]),
    subscribersTrialSongwriter: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_songwriter_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_songwriter_yearly')]),
    subscribersTrialPro: getSumDataSet([getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_pro_monthly'), getDataSetGroupedAvg(subscribersGrouped, 'subscribers_trial_pro_yearly')]),
  });

  const data = group ? getDataSetFromFilteredAndGrouped() : getDataSetFromFiltered();

  const fiveDaysAgoData = getFilteredData(analyticsEventsPerDay, 5);
  const songDisplayYouTubePerDay = getDataSet(fiveDaysAgoData, 'youtube_song_display');

  useEffect(() => {
    if (location?.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.classList.add('linked-to');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  }, [location]);

  // Express revenue
  const expectedExpressRevenue = [], expectedExpressRevenueIpad = [], expectedExpressRevenueIphone = [];
  for (let i = 0; i < data.loginExpressIpad.length; i++) {
    expectedExpressRevenue[i] = Math.round(data.loginExpress[i] * 2.99);
    expectedExpressRevenueIpad[i] = Math.round(data.loginExpressIpad[i] * 2.99);
    expectedExpressRevenueIphone[i] = Math.round(data.loginExpressIphone[i] * 2.99);
  }

  return (
    <section>
      {songDisplayYouTubePerDay.some(v => v < 5) && (
        <div className="alert alert-error">
          Warning! At least one of the last 5 days had less than 5 successful YouTube-imports.{' '}
          <Link to="/data/daily#youtube-finished">Check</Link> if something is wrong!
        </div>
      )}

      <div className="chart-grid">
        {/* Full width charts */}
        <div className="chart-col-full">
          <ChartCard header="User Registration per Client (Stacked)" loading={usersPerDay.loading} error={errors.usersPerDay}
            values={[data.usersMyScorecloud, data.usersStudio, data.usersExpress, data.usersSongwriter, data.usersUnknown]} height="350px"
            description="Number of new registered users per registration client">
            <Line data={chartConfig(data.usersLabels, ['My Scorecloud', 'Studio', 'Express', 'Songwriter', 'Unknown'],
              [data.usersMyScorecloud, data.usersStudio, data.usersExpress, data.usersSongwriter, data.usersUnknown],
              [colors.variant_lightgreen, colors.variant_lightblue, colors.variant_lightred, colors.variant_lightyellow, colors.variant_lightpurple], FILL, STACKED)}
              options={setOptions(STACKED, undefined, CUSTOMHEIGHT)} />
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard header="New Subscriptions (Stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[data.trialSubscriptionPlus, data.paidSubscriptionPlus, data.trialSubscriptionSongwriter, data.paidSubscriptionSongwriter, data.trialSubscriptionPro, data.paidSubscriptionPro]}
            height="350px" description="Number of users starting a specific subscription for the first time">
            <Line data={chartConfig(data.flagsLabels, ['Trial Plus', 'Paid Plus', 'Trial Songwriter', 'Paid Songwriter', 'Trial Pro', 'Paid Pro'],
              [data.trialSubscriptionPlus, data.paidSubscriptionPlus, data.trialSubscriptionSongwriter, data.paidSubscriptionSongwriter, data.trialSubscriptionPro, data.paidSubscriptionPro],
              [colors.variant_lightgreen, colors.calm_darkgreen, colors.variant_lightyellow, colors.calm_darkyellow, colors.variant_lightpurple, colors.calm_darkpurple], FILL, STACKED)}
              options={setOptions(STACKED, undefined, CUSTOMHEIGHT)} />
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard header="Revenue from Successfully charged subscriptions" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.subscriptionChargedSuccessfullySum]} height="350px" description="Sum in USD from Braintree">
            <Line data={chartConfig(data.analyticsEventsLabels, ['Earnings (USD)'], [data.subscriptionChargedSuccessfullySum], [colors.darkgreen])}
              options={setOptions(undefined, undefined, CUSTOMHEIGHT)} />
          </ChartCard>
        </div>

        {/* Half width charts */}
        <div className="chart-col">
          <ChartCard header="30 Day Action Users (Stacked)" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[data.activeUsersStudio, data.activeUsersSongwriter, data.activeUsersExpress]} hideTotal description="Number of users with any action within 30 days">
            <Line data={chartConfig(data.subscribersLabels, ['Studio', 'Songwriter', 'Express'],
              [data.activeUsersStudio, data.activeUsersSongwriter, data.activeUsersExpress],
              [colors.variant_lightblue, colors.variant_lightyellow, colors.variant_lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Number of Subscribers Paid" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[data.subscribersPaidPlus, data.subscribersPaidSongwriter, data.subscribersPaidPro]} hideTotal description="Snapshot of active paid subscriptions">
            <Line data={chartConfig(data.subscribersLabels, ['Paid Plus', 'Paid Songwriter', 'Paid Pro'],
              [data.subscribersPaidPlus, data.subscribersPaidSongwriter, data.subscribersPaidPro],
              [colors.teal, colors.yellow, colors.purple])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Revenue from successfully charged subscriptions (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.subscriptionChargedSuccessfullyPlusSum, data.subscriptionChargedSuccessfullySongwriterSum, data.subscriptionChargedSuccessfullyProSum]}
            description="Per plan (USD from Braintree)">
            <Line data={chartConfig(data.analyticsEventsLabels, ['Plus (USD)', 'Songwriter (USD)', 'Pro (USD)'],
              [data.subscriptionChargedSuccessfullyPlusSum, data.subscriptionChargedSuccessfullySongwriterSum, data.subscriptionChargedSuccessfullyProSum],
              [colors.calm_darkgreen, colors.calm_darkyellow, colors.calm_darkpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Estimated Revenue From ScoreCloud Express Installs" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone]}
            description="First login in Express × $2.99">
            <Line data={chartConfig(data.flagsLabels, ['Unknown (USD)', 'iPad (USD)', 'iPhone (USD)'],
              [expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone],
              [colors.lightblue, colors.blue, colors.darkblue], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Number of Subscribers Trial" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[data.subscribersTrialPlus, data.subscribersTrialSongwriter, data.subscribersTrialPro]} hideTotal description="Snapshot of active trial subscriptions">
            <Line data={chartConfig(data.subscribersLabels, ['Trial Plus', 'Trial Songwriter', 'Trial Pro'],
              [data.subscribersTrialPlus, data.subscribersTrialSongwriter, data.subscribersTrialPro],
              [colors.teal, colors.yellow, colors.purple])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Cloud Songs Per Client (Stacked)" loading={songsPerDay.loading} error={errors.songsPerDay}
            values={[data.newStudioSongs, data.newSongwriterSongs, data.newExpressSongs]}>
            <Line data={chartConfig(data.songsLabels, ['Studio', 'Songwriter', 'Express'],
              [data.newStudioSongs, data.newSongwriterSongs, data.newExpressSongs],
              [colors.darkteal, colors.darkyellow, colors.lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Songwriter Songs Saved" loading={songsPerDay.loading} error={errors.songsPerDay} values={[data.newSongwriterSongs]}>
            <Line data={chartConfig(data.songsLabels, ['Songs'], [data.newSongwriterSongs], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Studio Songs Saved" loading={songsPerDay.loading} error={errors.songsPerDay} values={[data.newStudioSongs]}>
            <Line data={chartConfig(data.songsLabels, ['Songs'], [data.newStudioSongs], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Studio and Songwriter Songs Saved" loading={songsPerDay.loading} error={errors.songsPerDay} values={[data.newStudioAndSongwriterSongs]}>
            <Line data={chartConfig(data.songsLabels, ['Songs'], [data.newStudioAndSongwriterSongs], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Express Songs Saved" loading={songsPerDay.loading} error={errors.songsPerDay} values={[data.newExpressSongs]}>
            <Line data={chartConfig(data.songsLabels, ['Songs'], [data.newExpressSongs], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>

        {/* Individual subscription charts */}
        <div className="chart-col">
          <ChartCard header="First time Plus Trial Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.trialSubscriptionPlus]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.trialSubscriptionPlus], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time Plus Paid Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.paidSubscriptionPlus]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.paidSubscriptionPlus], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time Songwriter Trial Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.trialSubscriptionSongwriter]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.trialSubscriptionSongwriter], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time Songwriter Paid Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.paidSubscriptionSongwriter]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.paidSubscriptionSongwriter], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time Pro Trial Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.trialSubscriptionPro]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.trialSubscriptionPro], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time Pro Paid Subscriptions" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.paidSubscriptionPro]}>
            <Line data={chartConfig(data.flagsLabels, ['Subscriptions'], [data.paidSubscriptionPro], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>

        {/* Login charts */}
        <div className="chart-col">
          <ChartCard header="First time login Studio (stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[data.loginStudio, data.loginStudioOsx, data.loginStudioOsxAppstore, data.loginStudioWin]}>
            <Line data={chartConfig(data.flagsLabels, ['Studio', 'Studio Osx', 'Studio Osx Appstore', 'Studio Win'],
              [data.loginStudio, data.loginStudioOsx, data.loginStudioOsxAppstore, data.loginStudioWin],
              [colors.blue, colors.darkblue, colors.darkgreen, colors.orange], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time login Songwriter (stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[data.loginSongwriter, data.loginSongwriterOsx, data.loginSongwriterOsxAppstore, data.loginSongwriterWin]}>
            <Line data={chartConfig(data.flagsLabels, ['Songwriter', 'Songwriter Osx', 'Songwriter Osx Appstore', 'Songwriter Win'],
              [data.loginSongwriter, data.loginSongwriterOsx, data.loginSongwriterOsxAppstore, data.loginSongwriterWin],
              [colors.darkyellow, colors.yellow, colors.purple, colors.secondary], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time login Express (stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[data.loginExpress, data.loginExpressIpad, data.loginExpressIphone]}>
            <Line data={chartConfig(data.flagsLabels, ['Express', 'Express Ipad', 'Express Iphone'],
              [data.loginExpress, data.loginExpressIpad, data.loginExpressIphone],
              [colors.yellow, colors.purple, colors.lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="First time login My Scorecloud" loading={flagsPerDay.loading} error={errors.flagsPerDay} values={[data.loginMyScorecloud]}>
            <Line data={chartConfig(data.flagsLabels, ['My Scorecloud'], [data.loginMyScorecloud], [colors.teal], FILL)} options={setOptions()} />
          </ChartCard>
        </div>

        {/* Analytics charts */}
        <div className="chart-col">
          <ChartCard header="Songwriter recordings" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.recordingStart]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Recordings'], [data.recordingStart], [colors.orange])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Songwriter audio imports" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.audioImport]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Audio imports'], [data.audioImport], [colors.darkblue])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Songwriter YouTube imports" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.videoImport]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['YouTube imports'], [data.videoImport], [colors.darkred])} options={setOptions()} />
          </ChartCard>
        </div>
        <div id="youtube-finished" className="chart-col">
          <ChartCard header="Songwriter YouTube import analysis finished events" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.songDisplayYouTube]} description="song-display events">
            <Line data={chartConfig(data.analyticsEventsLabels, ['Finished'], [data.songDisplayYouTube], [colors.lightred])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Songwriter analysis started events" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.recordingPlusImport]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Started'], [data.recordingPlusImport], [colors.purple])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Songwriter analysis finished events" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.songDisplay]} description="song-display events">
            <Line data={chartConfig(data.analyticsEventsLabels, ['Finished'], [data.songDisplay], [colors.green])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Songwriter app starts" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.programStartSongwriter]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['App starts'], [data.programStartSongwriter], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="New Registered User Accounts" loading={usersPerDay.loading} error={errors.usersPerDay} values={[data.totalUsers]}>
            <Line data={chartConfig(data.usersLabels, ['Users'], [data.totalUsers], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Subscription start" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.subscriptionStart]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Subscriptions'], [data.subscriptionStart], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Subscription charged successfully" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.subscriptionChargedSuccessfully]} description="New and recurring payments">
            <Line data={chartConfig(data.analyticsEventsLabels, ['Subscriptions'], [data.subscriptionChargedSuccessfully], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Subscription upgrade" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.subscriptionUpgrade]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Subscriptions'], [data.subscriptionUpgrade], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Subscription downgrade" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.subscriptionDowngrade]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Subscriptions'], [data.subscriptionDowngrade], [colors.secondary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Subscription canceled" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay} values={[data.subscriptionCancelled]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Subscriptions'], [data.subscriptionCancelled], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="Web Downloads (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[data.webDownloadSongwriterMac, data.webDownloadSongwriterWin, data.webDownloadStudioMac, data.webDownloadStudioWin]}>
            <Line data={chartConfig(data.analyticsEventsLabels, ['Songwriter Mac', 'Songwriter Win', 'Studio Mac', 'Studio Win'],
              [data.webDownloadSongwriterMac, data.webDownloadSongwriterWin, data.webDownloadStudioMac, data.webDownloadStudioWin],
              [colors.yellow, colors.darkyellow, colors.teal, colors.darkteal], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
      </div>
    </section>
  );
};

export default DashboardFilteredAndGrouped;
